import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { ROOT_URL } from "../../api/ApiConfig";
import {
  mapApiAgentToTableRow,
  normalizeCoUserRecord,
} from "../../utils/agentMappers";

const getAuthHeaders = (getState) => {
  const { loginToken } = getState();
  const authToken = loginToken?.response?.token;
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${authToken}`,
  };
};

const unwrap = (response) => response?.data;

function mapListResponse(payload) {
  const data = payload?.data;
  if (!Array.isArray(data)) return [];
  return data.map(mapApiAgentToTableRow).filter(Boolean);
}

function buildAgentsListKey(arg = {}) {
  const {
    status,
    role,
    team_id,
    group_id,
    search,
    last_activity_days,
  } = arg;
  return [
    status || "all",
    role || "",
    team_id || "",
    group_id || "",
    search || "",
    last_activity_days || "",
  ].join("::");
}

function normalizeAgentDetail(data) {
  const user = normalizeCoUserRecord(data) || data || null;
  if (!user) return null;
  return { ...user, id: user.id || user._id };
}

export const fetchAgentsList = createAsyncThunk(
  "agents/fetchList",
  async (
    { status, role, team_id, group_id, search, last_activity_days, countsOnly } = {},
    { getState, rejectWithValue, signal }
  ) => {
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (role) params.set("role", role);
      if (team_id) params.set("team_id", team_id);
      if (group_id) params.set("group_id", group_id);
      if (search) params.set("search", search);
      if (last_activity_days) params.set("last_activity_days", String(last_activity_days));

      const qs = params.toString();
      const url = `${ROOT_URL}/co-users/list${qs ? `?${qs}` : ""}`;
      const response = await axios.get(url, {
        headers: getAuthHeaders(getState),
        signal,
      });
      return { ...unwrap(response), _queryStatus: status || "all" };
    } catch (error) {
      if (axios.isCancel(error) || error.name === "CanceledError" || signal?.aborted) {
        return rejectWithValue({ canceled: true });
      }
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchAgentById = createAsyncThunk(
  "agents/fetchById",
  async ({ agentId }, { getState, rejectWithValue }) => {
    try {
      const response = await axios.get(`${ROOT_URL}/co-users/list/${agentId}`, {
        headers: getAuthHeaders(getState),
      });
      return unwrap(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const inviteAgent = createAsyncThunk(
  "agents/invite",
  async (body, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(`${ROOT_URL}/co-users/send-invitation`, body, {
        headers: getAuthHeaders(getState),
      });
      return unwrap(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateAgent = createAsyncThunk(
  "agents/update",
  async ({ agentId, body }, { getState, rejectWithValue }) => {
    try {
      const response = await axios.put(`${ROOT_URL}/co-users/update/${agentId}`, body, {
        headers: getAuthHeaders(getState),
      });
      return unwrap(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateAgentAcceptingChats = createAsyncThunk(
  "agents/updateAccepting",
  async ({ agentId, accept_chat }, { getState, rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${ROOT_URL}/co-users/accepting-chats/${agentId}`,
        { accept_chat },
        { headers: getAuthHeaders(getState) }
      );
      return unwrap(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const assignAgentTeams = createAsyncThunk(
  "agents/assignTeams",
  async ({ agentId, team_ids = [] }, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${ROOT_URL}/co-users/assign-teams/${agentId}`,
        { team_ids },
        { headers: getAuthHeaders(getState) }
      );
      return unwrap(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const suspendAgent = createAsyncThunk(
  "agents/suspend",
  async ({ agentId }, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${ROOT_URL}/co-users/suspend`,
        { id: agentId },
        { headers: getAuthHeaders(getState) }
      );
      return unwrap(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const unSuspendAgent = createAsyncThunk(
  "agents/unSuspend",
  async ({ agentId }, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${ROOT_URL}/co-users/unSuspend`,
        { id: agentId },
        { headers: getAuthHeaders(getState) }
      );
      return unwrap(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteAgent = createAsyncThunk(
  "agents/delete",
  async ({ agentId }, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${ROOT_URL}/co-users/delete`,
        { id: agentId },
        { headers: getAuthHeaders(getState) }
      );
      return unwrap(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const bulkAgents = createAsyncThunk(
  "agents/bulk",
  async (body, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(`${ROOT_URL}/co-users/bulk`, body, {
        headers: getAuthHeaders(getState),
      });
      return unwrap(response);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  list: [],
  listLoading: false,
  listError: null,
  lastFetchedListKey: null,
  cacheByListKey: {},
  tabTotals: {},
  detail: null,
  detailLoading: false,
  detailError: null,
  lastFetchedAgentId: null,
  cacheByAgentId: {},
  mutationLoading: false,
  mutationError: null,
};

const agentsApiSlice = createSlice({
  name: "peopleAgents",
  initialState,
  reducers: {
    clearAgentDetail: (state) => {
      state.detail = null;
      state.detailError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAgentsList.pending, (state, action) => {
        if (action.meta?.arg?.countsOnly) return;
        const key = buildAgentsListKey(action.meta?.arg);
        const cached = key && state.cacheByListKey[key];
        if (cached) {
          state.list = cached;
          state.listLoading = false;
          state.lastFetchedListKey = key;
        } else if (key && key !== state.lastFetchedListKey) {
          state.listLoading = true;
          state.list = [];
        } else {
          state.listLoading = state.list.length === 0;
        }
        state.listError = null;
      })
      .addCase(fetchAgentsList.fulfilled, (state, action) => {
        const statusKey = action.payload?._queryStatus || action.meta?.arg?.status || "all";
        const mapped = mapListResponse(action.payload);
        state.tabTotals[statusKey] = mapped.length;
        if (!action.meta?.arg?.countsOnly) {
          const key = buildAgentsListKey(action.meta?.arg);
          state.listLoading = false;
          state.list = mapped;
          if (key) {
            state.cacheByListKey[key] = mapped;
            state.lastFetchedListKey = key;
          }
        }
      })
      .addCase(fetchAgentsList.rejected, (state, action) => {
        if (action.meta?.arg?.countsOnly) return;
        if (action.meta?.aborted || action.payload?.canceled) return;
        const key = buildAgentsListKey(action.meta?.arg);
        state.listLoading = false;
        state.listError = action.payload;
        if (!key || !state.cacheByListKey[key]) {
          state.list = [];
        }
      })
      .addCase(fetchAgentById.pending, (state, action) => {
        const idStr =
          action.meta.arg?.agentId != null ? String(action.meta.arg.agentId) : "";
        const cached = idStr && state.cacheByAgentId[idStr];
        const detailId = state.detail?.id || state.detail?._id;
        const inMemorySame =
          idStr &&
          detailId != null &&
          String(detailId) === idStr &&
          state.lastFetchedAgentId != null &&
          String(state.lastFetchedAgentId) === idStr;
        if (cached) {
          state.detail = cached;
          state.detailLoading = false;
        } else if (inMemorySame) {
          state.detailLoading = false;
        } else {
          state.detailLoading = true;
        }
        state.detailError = null;
      })
      .addCase(fetchAgentById.fulfilled, (state, action) => {
        state.detailLoading = false;
        const data = action.payload?.data;
        state.detail = normalizeAgentDetail(data);
        const idStr =
          action.meta.arg?.agentId != null ? String(action.meta.arg.agentId) : "";
        if (idStr && state.detail) {
          state.cacheByAgentId[idStr] = state.detail;
          state.lastFetchedAgentId = idStr;
        }
      })
      .addCase(fetchAgentById.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError = action.payload;
        const idStr =
          action.meta.arg?.agentId != null ? String(action.meta.arg.agentId) : "";
        if (!idStr || !state.cacheByAgentId[idStr]) {
          state.detail = null;
        }
      })
      .addMatcher(
        (action) =>
          [
            inviteAgent.pending.type,
            updateAgent.pending.type,
            updateAgentAcceptingChats.pending.type,
            assignAgentTeams.pending.type,
            suspendAgent.pending.type,
            unSuspendAgent.pending.type,
            deleteAgent.pending.type,
            bulkAgents.pending.type,
          ].includes(action.type),
        (state) => {
          state.mutationLoading = true;
          state.mutationError = null;
        }
      )
      .addMatcher(
        (action) =>
          [
            inviteAgent.fulfilled.type,
            inviteAgent.rejected.type,
            updateAgent.fulfilled.type,
            updateAgent.rejected.type,
            updateAgentAcceptingChats.fulfilled.type,
            updateAgentAcceptingChats.rejected.type,
            assignAgentTeams.fulfilled.type,
            assignAgentTeams.rejected.type,
            suspendAgent.fulfilled.type,
            suspendAgent.rejected.type,
            unSuspendAgent.fulfilled.type,
            unSuspendAgent.rejected.type,
            deleteAgent.fulfilled.type,
            deleteAgent.rejected.type,
            bulkAgents.fulfilled.type,
            bulkAgents.rejected.type,
          ].includes(action.type),
        (state, action) => {
          state.mutationLoading = false;
          if (action.type.endsWith("/rejected")) {
            state.mutationError = action.payload;
          }
        }
      );
  },
});

export const { clearAgentDetail } = agentsApiSlice.actions;
export default agentsApiSlice.reducer;
